/**
 * @fileoverview Automation rule builder dialog (MOR-736)
 *
 * WHEN (trigger event + filters) → IF (job conditions) → THEN (actions)
 * builder used by the AutomationsManager for both create and edit. Status
 * references are stored by org status `key` (portable), so the selects here
 * map key ⇄ status via the org's `taskStatuses`.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  AutomationAction,
  AutomationEventType,
  AutomationNotifyTarget,
  AutomationTrigger,
} from "@cvx/automations/automationRules/schema";
import type { AppointmentType, JobStatus } from "@cvx/jobs/schema";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { MemberSelect } from "@/components/settings/tasks/MemberSelect";
import { MemberOrRoleSelect } from "@/components/settings/tasks/MemberOrRoleSelect";
import { WorkflowRoleSelect } from "@/components/settings/tasks/WorkflowRoleSelect";
import { APPOINTMENT_TYPE_TAGS } from "@/lib/precedent-templates";
import { DATA_AGENTS } from "@/components/jobs/phases/gather/summarization/agentRegistry";
import { extractErrorMessage } from "@/utils/errors";
import {
  ACTION_OPTIONS,
  EVENT_BY_VALUE,
  EVENT_OPTIONS,
  JOB_STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  SEARCH_KIND_OPTIONS,
  SEND_STATUS_OPTIONS,
  defaultActionFor,
  type ActionType,
} from "./automation-config";

type Conditions = {
  appointmentTypes?: AppointmentType[];
  jobStatuses?: JobStatus[];
};

/** Shared datalist id for workflow task-title suggestions. */
const TASK_TITLE_LIST_ID = "automation-task-title-options";

/** Sentinel for the workflow task picker's "no specific task" option. */
const ANY_TASK = "__any_task__";

export interface EditableRule {
  _id?: Id<"automationRules">;
  templateId?: Id<"taskTemplates">;
  templateName?: string;
  jobId?: Id<"jobs">;
  jobName?: string;
  name: string;
  description?: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  conditions?: Conditions;
  actions: AutomationAction[];
}

export function AutomationRuleDialog({
  orgId,
  open,
  onOpenChange,
  initial,
  templateId,
  templateName,
  jobId,
  jobName,
  workflowTaskTitles,
  defaultTaskTitleContains,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for "new rule". */
  initial?: EditableRule;
  /** Build the rule into this workflow template (MOR-736). New rules are
   *  created scoped to the workflow; existing rules keep their scope. */
  templateId?: Id<"taskTemplates">;
  templateName?: string;
  /** Scope a new rule to a single job (MOR-736) — used when adding an
   *  automation from a job task's side panel. Mutually exclusive with
   *  `templateId`. */
  jobId?: Id<"jobs">;
  jobName?: string;
  /** The workflow's task titles — offered as suggestions in title filters. */
  workflowTaskTitles?: string[];
  /** Pre-fill the task-title filter once a task event is chosen (quick-add
   *  from a task card) — the event itself is left unselected so the user still
   *  picks the trigger deliberately. */
  defaultTaskTitleContains?: string;
}) {
  const createRule = useConvexMutation(api.automations.rules.create);
  const updateRule = useConvexMutation(api.automations.rules.update);

  const { data: statuses } = useQuery(
    convexQuery(api.tasks.statuses.listByOrg, { orgId }),
  );
  const { data: productCatalog } = useQuery(
    convexQuery(api.jobs.searches.queries.listInfotrackProductCatalog, {}),
  );

  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  // No event is preselected for new rules — the user picks one deliberately.
  const [trigger, setTrigger] = useState<AutomationTrigger | undefined>(
    initial?.trigger,
  );
  const [conditions, setConditions] = useState<Conditions>(
    initial?.conditions ?? {},
  );
  const [actions, setActions] = useState<AutomationAction[]>(
    initial?.actions ?? [],
  );
  const [saving, setSaving] = useState(false);

  const eventMeta = trigger ? EVENT_BY_VALUE.get(trigger.event) : undefined;
  const workflowScopeName = templateName ?? initial?.templateName;
  const jobScopeName = jobName ?? initial?.jobName;

  const statusOptions = useMemo(
    () =>
      (statuses ?? []).map((s) => ({
        value: s.key,
        label: s.name,
        color: s.color,
      })),
    [statuses],
  );
  const agentOptions = useMemo(
    () => DATA_AGENTS.map((a) => ({ value: a.id, label: a.label })),
    [],
  );
  const productOptions = useMemo(
    () =>
      (productCatalog ?? []).map((p: { key: string; displayName: string }) => ({
        value: p.key,
        label: p.displayName,
      })),
    [productCatalog],
  );
  const appointmentTypeOptions = useMemo(
    () =>
      APPOINTMENT_TYPE_TAGS.map((t) => ({ value: t.value, label: t.label })),
    [],
  );

  const setEvent = (value: string) => {
    const event = value as AutomationEventType;
    // Reset filters that don't apply to the new event.
    const meta = EVENT_BY_VALUE.get(event);
    const next: AutomationTrigger = { event };
    if (meta?.filters.includes("taskTitleContains")) {
      // Keep any title the user already set; otherwise seed the quick-add task.
      const seededTitle =
        trigger?.taskTitleContains ?? defaultTaskTitleContains?.trim();
      if (seededTitle) next.taskTitleContains = seededTitle;
    }
    if (meta?.filters.includes("statusKeys") && trigger?.statusKeys) {
      next.statusKeys = trigger.statusKeys;
    }
    setTrigger(next);
    // Drop actions that no longer apply (e.g. set_report_status on a task event).
    setActions((prev) =>
      prev
        .filter((a) => {
          const opt = ACTION_OPTIONS.find((o) => o.value === a.type);
          return !opt?.allowedEvents || opt.allowedEvents(event);
        })
        // "The triggering task" stops existing on non-task events — fall
        // back to a title match instead of keeping a dangling reference.
        .map((a) =>
          a.type === "update_tasks" &&
          a.match.kind === "trigger_task" &&
          !meta?.hasTask
            ? { ...a, match: { kind: "title_contains" as const, text: "" } }
            : a,
        ),
    );
  };

  const updateAction = (index: number, next: AutomationAction) =>
    setActions((prev) => prev.map((a, i) => (i === index ? next : a)));
  const removeAction = (index: number) =>
    setActions((prev) => prev.filter((_, i) => i !== index));

  const availableActionOptions = ACTION_OPTIONS.filter(
    (o) =>
      !o.allowedEvents || (trigger ? o.allowedEvents(trigger.event) : false),
  );

  const validate = (): string | null => {
    if (!name.trim()) return "Give the rule a name.";
    if (!trigger) return "Pick an event for the “When” trigger.";
    if (actions.length === 0) return "Add at least one action.";
    for (const a of actions) {
      if (a.type === "create_task" && !a.title.trim()) {
        return "The create-task action needs a task title.";
      }
      if (
        a.type === "update_tasks" &&
        a.match.kind === "title_contains" &&
        !a.match.text.trim()
      ) {
        return "The update-tasks action needs a title to match.";
      }
      if (
        a.type === "update_tasks" &&
        a.match.kind === "trigger_task" &&
        !eventMeta?.hasTask
      ) {
        return "This event has no triggering task — match tasks by title instead.";
      }
      if (a.type === "send_notification" && !a.message.trim()) {
        return "The notification action needs a message.";
      }
      if (
        (a.type === "send_notification" || a.type === "start_reminder") &&
        ((a.to.kind === "user" && !a.to.userId) ||
          (a.to.kind === "role" && !a.to.roleId))
      ) {
        return "Pick who the automation should notify.";
      }
      if (a.type === "start_reminder" && a.untilStatusKeys.length === 0) {
        return "The reminder needs at least one “until” status.";
      }
      if (
        (a.type === "set_day_one_status" ||
          a.type === "set_report_status" ||
          a.type === "set_agent_status") &&
        !a.statusKey
      ) {
        return "Pick the status to apply.";
      }
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error || !trigger) {
      toast.error(error ?? "Pick an event for the “When” trigger.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        trigger,
        conditions:
          conditions.appointmentTypes?.length || conditions.jobStatuses?.length
            ? {
                appointmentTypes: conditions.appointmentTypes?.length
                  ? conditions.appointmentTypes
                  : undefined,
                jobStatuses: conditions.jobStatuses?.length
                  ? conditions.jobStatuses
                  : undefined,
              }
            : undefined,
        actions,
        enabled,
      };
      if (initial?._id) {
        await updateRule({
          ruleId: initial._id,
          patch: { ...payload, conditions: payload.conditions ?? null },
        });
        toast.success("Automation updated.");
      } else {
        await createRule({
          orgId,
          templateId: templateId ?? initial?.templateId,
          jobId: jobId ?? initial?.jobId,
          ...payload,
        });
        toast.success("Automation created.");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save the automation."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle>
            {initial?._id ? "Edit automation" : "New automation"}
          </DialogTitle>
          <DialogDescription>
            {workflowScopeName
              ? `Part of the “${workflowScopeName}” workflow — only runs on jobs this workflow has been applied to.`
              : jobScopeName
                ? `Only runs on the “${jobScopeName}” job.`
                : "When something happens on the platform, run actions automatically on every job."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Task-title suggestions from the workflow being edited. */}
          {workflowTaskTitles && workflowTaskTitles.length > 0 && (
            <datalist id={TASK_TITLE_LIST_ID}>
              {[...new Set(workflowTaskTitles.filter((t) => t.trim()))].map(
                (title) => (
                  <option key={title} value={title} />
                ),
              )}
            </datalist>
          )}

          {/* Name / description */}
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="automation-name">Name</Label>
              <Input
                id="automation-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Assign Juli when day ones are in review"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="automation-description">
                Description{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </Label>
              <Input
                id="automation-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this automation is for"
              />
            </div>
          </div>

          {/* WHEN */}
          <section className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-950/30">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-900"
              >
                When
              </Badge>
              <span className="text-sm text-muted-foreground">
                this happens on a job
              </span>
            </div>
            <Select value={trigger?.event ?? ""} onValueChange={setEvent}>
              <SelectTrigger>
                <SelectValue placeholder="Select an event" />
              </SelectTrigger>
              <SelectContent>
                {["Tasks", "Day one", "Reports", "Agents", "Searches"].map(
                  (group) => (
                    <div key={group}>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {group}
                      </div>
                      {EVENT_OPTIONS.filter((e) => e.group === group).map(
                        (e) => (
                          <SelectItem key={e.value} value={e.value}>
                            {e.label}
                          </SelectItem>
                        ),
                      )}
                    </div>
                  ),
                )}
              </SelectContent>
            </Select>

            {trigger &&
              eventMeta?.filters.includes("taskTitleContains") &&
              (workflowTaskTitles && workflowTaskTitles.length > 0 ? (
                // Inside a workflow the tasks are known — offer them as a
                // picker instead of a free-text "contains" match.
                <div className="grid gap-1.5">
                  <Label>
                    Select task this automation applies to (optional)
                  </Label>
                  <Select
                    value={trigger.taskTitleContains ?? ANY_TASK}
                    onValueChange={(v) =>
                      setTrigger({
                        ...trigger,
                        taskTitleContains: v === ANY_TASK ? undefined : v,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any task" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY_TASK}>Any task</SelectItem>
                      {/* Keep a stored value visible even if the task was
                          renamed or the filter was saved as a substring. */}
                      {trigger.taskTitleContains &&
                        !workflowTaskTitles.includes(
                          trigger.taskTitleContains,
                        ) && (
                          <SelectItem value={trigger.taskTitleContains}>
                            Titles containing “{trigger.taskTitleContains}”
                          </SelectItem>
                        )}
                      {[
                        ...new Set(workflowTaskTitles.filter((t) => t.trim())),
                      ].map((title) => (
                        <SelectItem key={title} value={title}>
                          {title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    If no task is selected, the automation will be triggered
                    generally for <strong>every task</strong> on jobs using the
                    workflow.
                  </p>
                </div>
              ) : (
                <div className="grid gap-1.5">
                  <Label>Task title contains (optional)</Label>
                  <Input
                    value={trigger.taskTitleContains ?? ""}
                    onChange={(e) =>
                      setTrigger({
                        ...trigger,
                        taskTitleContains: e.target.value || undefined,
                      })
                    }
                    placeholder="e.g. draft report to creditors"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Leave empty to run for every task that matches the event.
                  </p>
                </div>
              ))}
            {trigger && eventMeta?.filters.includes("statusKeys") && (
              <div className="grid gap-1.5">
                <Label>Only these statuses (optional)</Label>
                <MultiSearchableSelect
                  value={trigger.statusKeys ?? []}
                  onValueChange={(v) =>
                    setTrigger({
                      ...trigger,
                      statusKeys: v.length > 0 ? v : undefined,
                    })
                  }
                  options={statusOptions}
                  placeholder="Any status"
                />
              </div>
            )}
            {trigger && eventMeta?.filters.includes("sendStatuses") && (
              <div className="grid gap-1.5">
                <Label>Only these send statuses (optional)</Label>
                <MultiSearchableSelect
                  value={trigger.sendStatuses ?? []}
                  onValueChange={(v) =>
                    setTrigger({
                      ...trigger,
                      sendStatuses:
                        v.length > 0
                          ? (v as AutomationTrigger["sendStatuses"])
                          : undefined,
                    })
                  }
                  options={SEND_STATUS_OPTIONS.map((o) => ({ ...o }))}
                  placeholder="Any send status"
                />
              </div>
            )}
            {trigger && eventMeta?.filters.includes("agentIds") && (
              <div className="grid gap-1.5">
                <Label>Only these agents (optional)</Label>
                <MultiSearchableSelect
                  value={trigger.agentIds ?? []}
                  onValueChange={(v) =>
                    setTrigger({
                      ...trigger,
                      agentIds: v.length > 0 ? v : undefined,
                    })
                  }
                  options={agentOptions}
                  placeholder="Any agent"
                />
              </div>
            )}
            {trigger && eventMeta?.filters.includes("searchKinds") && (
              <div className="grid gap-1.5">
                <Label>Only these search types (optional)</Label>
                <MultiSearchableSelect
                  value={trigger.searchKinds ?? []}
                  onValueChange={(v) =>
                    setTrigger({
                      ...trigger,
                      searchKinds:
                        v.length > 0
                          ? (v as AutomationTrigger["searchKinds"])
                          : undefined,
                    })
                  }
                  options={SEARCH_KIND_OPTIONS.map((o) => ({ ...o }))}
                  placeholder="Any search type"
                />
              </div>
            )}
            {trigger && eventMeta?.filters.includes("productKeys") && (
              <div className="grid gap-1.5">
                <Label>Only these products (optional)</Label>
                <MultiSearchableSelect
                  value={trigger.productKeys ?? []}
                  onValueChange={(v) =>
                    setTrigger({
                      ...trigger,
                      productKeys: v.length > 0 ? v : undefined,
                    })
                  }
                  options={productOptions}
                  placeholder="Any product"
                />
              </div>
            )}
          </section>

          {/* IF */}
          <section className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200 dark:hover:bg-amber-900"
              >
                If
              </Badge>
              <span className="text-sm text-muted-foreground">
                the job matches (optional)
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>Appointment types</Label>
                <MultiSearchableSelect
                  value={conditions.appointmentTypes ?? []}
                  onValueChange={(v) =>
                    setConditions({
                      ...conditions,
                      appointmentTypes:
                        v.length > 0 ? (v as AppointmentType[]) : undefined,
                    })
                  }
                  options={appointmentTypeOptions}
                  placeholder="Any appointment type"
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Job statuses</Label>
                <MultiSearchableSelect
                  value={conditions.jobStatuses ?? []}
                  onValueChange={(v) =>
                    setConditions({
                      ...conditions,
                      jobStatuses:
                        v.length > 0 ? (v as JobStatus[]) : undefined,
                    })
                  }
                  options={JOB_STATUS_OPTIONS.map((o) => ({ ...o }))}
                  placeholder="Any job status"
                />
              </div>
            </div>
          </section>

          {/* THEN */}
          <section className="space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="border-transparent bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200 dark:hover:bg-emerald-900"
                >
                  Then
                </Badge>
                <span className="text-sm text-muted-foreground">
                  run these actions
                </span>
              </div>
              <Select
                value=""
                onValueChange={(v) =>
                  setActions((prev) => {
                    const action = defaultActionFor(v as ActionType);
                    // "The triggering task" only exists for task events —
                    // default to a title match everywhere else.
                    if (action.type === "update_tasks" && !eventMeta?.hasTask) {
                      action.match = { kind: "title_contains", text: "" };
                    }
                    return [...prev, action];
                  })
                }
              >
                <SelectTrigger className="w-auto gap-1.5">
                  <Plus className="size-3.5" />
                  <span className="text-sm">Add action</span>
                </SelectTrigger>
                <SelectContent>
                  {availableActionOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {actions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No actions yet — add one above.
              </p>
            ) : (
              <div className="space-y-3">
                {actions.map((action, index) => (
                  <ActionEditor
                    key={index}
                    orgId={orgId}
                    action={action}
                    statusOptions={statusOptions}
                    eventHasTask={eventMeta?.hasTask ?? false}
                    triggerTaskTitle={trigger?.taskTitleContains}
                    taskTitleListId={
                      workflowTaskTitles?.length
                        ? TASK_TITLE_LIST_ID
                        : undefined
                    }
                    onChange={(next) => updateAction(index, next)}
                    onRemove={() => removeAction(index)}
                  />
                ))}
              </div>
            )}
          </section>

          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-medium text-primary">Enabled</p>
              <p className="text-xs text-muted-foreground">
                Disabled automations keep their configuration but never run.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>

        <DialogFooter className="border-t border-border px-6 py-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving
              ? "Saving…"
              : initial?._id
                ? "Save changes"
                : "Create automation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Per-action editor
// ---------------------------------------------------------------------------

function ActionEditor({
  orgId,
  action,
  statusOptions,
  eventHasTask,
  triggerTaskTitle,
  taskTitleListId,
  onChange,
  onRemove,
}: {
  orgId: Id<"organizations">;
  action: AutomationAction;
  statusOptions: { value: string; label: string }[];
  eventHasTask: boolean;
  /** The specific task selected in the "When" trigger, if any (workflow scope). */
  triggerTaskTitle?: string;
  /** Datalist id offering the workflow's task titles as suggestions. */
  taskTitleListId?: string;
  onChange: (next: AutomationAction) => void;
  onRemove: () => void;
}) {
  const option = ACTION_OPTIONS.find((o) => o.value === action.type);

  // Task events always carry a triggering task, so an "update tasks" action
  // acts on it directly — keep the match locked to the triggering task and
  // don't offer the (inapplicable) title-contains matcher.
  useEffect(() => {
    if (
      action.type === "update_tasks" &&
      eventHasTask &&
      action.match.kind !== "trigger_task"
    ) {
      onChange({ ...action, match: { kind: "trigger_task" } });
    }
  }, [action, eventHasTask, onChange]);

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-primary">{option?.label}</p>
          <p className="text-xs text-muted-foreground">{option?.description}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onRemove} title="Remove">
          <Trash2 className="size-4 text-destructive" />
        </Button>
      </div>

      {action.type === "create_task" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Task title</Label>
            <Input
              value={action.title}
              onChange={(e) => onChange({ ...action, title: e.target.value })}
              placeholder="e.g. Send day one letters"
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Description (optional)</Label>
            <Textarea
              rows={2}
              value={action.description ?? ""}
              onChange={(e) =>
                onChange({
                  ...action,
                  description: e.target.value || undefined,
                })
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Assignee</Label>
            <MemberOrRoleSelect
              orgId={orgId}
              value={{
                userId: action.assigneeUserId ?? null,
                roleId: action.assigneeRoleId ?? null,
              }}
              onChange={(next) =>
                onChange({
                  ...action,
                  assigneeUserId: next.userId ?? undefined,
                  assigneeRoleId: next.roleId ?? undefined,
                })
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Priority</Label>
            <Select
              value={
                action.priority !== undefined ? String(action.priority) : "none"
              }
              onValueChange={(v) =>
                onChange({
                  ...action,
                  priority: v === "none" ? undefined : Number(v),
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Status</Label>
            <StatusSelect
              value={action.statusKey}
              statusOptions={statusOptions}
              allowNone
              onChange={(v) => onChange({ ...action, statusKey: v })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Due in (days)</Label>
            <Input
              type="number"
              min={0}
              value={action.dueInDays ?? ""}
              onChange={(e) =>
                onChange({
                  ...action,
                  dueInDays:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              placeholder="7"
            />
          </div>
        </div>
      )}

      {action.type === "update_tasks" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Which tasks</Label>
            {eventHasTask ? (
              // A task event always has a triggering task — act on it directly.
              <>
                <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  {triggerTaskTitle?.trim()
                    ? triggerTaskTitle
                    : "The task from the “When” event above"}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Acts on the exact task the “When” event happened to.
                </p>
              </>
            ) : (
              <Input
                value={
                  action.match.kind === "title_contains"
                    ? action.match.text
                    : ""
                }
                onChange={(e) =>
                  onChange({
                    ...action,
                    match: { kind: "title_contains", text: e.target.value },
                  })
                }
                placeholder="e.g. day one"
                list={taskTitleListId}
              />
            )}
          </div>
          <div className="grid gap-1.5">
            <Label>Set status</Label>
            <StatusSelect
              value={action.setStatusKey}
              statusOptions={statusOptions}
              allowNone
              onChange={(v) => onChange({ ...action, setStatusKey: v })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Set assignee</Label>
            <MemberOrRoleSelect
              orgId={orgId}
              value={{
                userId: action.setAssigneeUserId ?? null,
                roleId: action.setAssigneeRoleId ?? null,
              }}
              onChange={(next) =>
                onChange({
                  ...action,
                  setAssigneeUserId: next.userId ?? undefined,
                  setAssigneeRoleId: next.roleId ?? undefined,
                })
              }
              placeholder="Leave unchanged"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Set priority</Label>
            <Select
              value={
                action.setPriority !== undefined
                  ? String(action.setPriority)
                  : "none"
              }
              onValueChange={(v) =>
                onChange({
                  ...action,
                  setPriority: v === "none" ? undefined : Number(v),
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Leave unchanged</SelectItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={String(p.value)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {action.type === "send_notification" && (
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Send to</Label>
            <NotifyTargetSelect
              orgId={orgId}
              value={action.to}
              onChange={(to) => onChange({ ...action, to })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Message</Label>
            <Textarea
              rows={2}
              value={action.message}
              onChange={(e) => onChange({ ...action, message: e.target.value })}
              placeholder="e.g. Day one letters are ready for review."
            />
          </div>
        </div>
      )}

      {action.type === "start_reminder" && (
        <div className="grid gap-3 sm:grid-cols-2">
          {!eventHasTask && (
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>Remind about the task titled…</Label>
              <Input
                value={action.taskTitleContains ?? ""}
                onChange={(e) =>
                  onChange({
                    ...action,
                    taskTitleContains: e.target.value || undefined,
                  })
                }
                placeholder="e.g. draft report to creditors"
                list={taskTitleListId}
              />
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Frequency</Label>
            <Select
              value={action.frequency}
              onValueChange={(v) =>
                onChange({
                  ...action,
                  frequency: v as "daily" | "weekly",
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Stop after (days, optional)</Label>
            <Input
              type="number"
              min={1}
              value={action.expiresInDays ?? ""}
              onChange={(e) =>
                onChange({
                  ...action,
                  expiresInDays:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              placeholder="21"
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Until the task status is one of</Label>
            <MultiSearchableSelect
              value={action.untilStatusKeys}
              onValueChange={(v) => onChange({ ...action, untilStatusKeys: v })}
              options={statusOptions}
              placeholder="Pick target statuses"
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Remind</Label>
            <NotifyTargetSelect
              orgId={orgId}
              value={action.to}
              onChange={(to) => onChange({ ...action, to })}
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Custom message (optional)</Label>
            <Input
              value={action.message ?? ""}
              onChange={(e) =>
                onChange({ ...action, message: e.target.value || undefined })
              }
              placeholder="Defaults to a reminder about the task"
            />
          </div>
        </div>
      )}

      {action.type === "approve_searches" && (
        <div className="grid gap-1.5">
          <Label>Which recommendations</Label>
          <Select
            value={action.scope}
            onValueChange={(v) =>
              onChange({
                ...action,
                scope: v as "determinative" | "all_pending",
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="determinative">
                Determinative searches only
              </SelectItem>
              <SelectItem value="all_pending">All pending searches</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {(action.type === "set_day_one_status" ||
        action.type === "set_report_status" ||
        action.type === "set_agent_status") && (
        <div className="grid gap-1.5">
          <Label>Status to apply</Label>
          <StatusSelect
            value={action.statusKey || undefined}
            statusOptions={statusOptions}
            onChange={(v) => onChange({ ...action, statusKey: v ?? "" })}
          />
        </div>
      )}
    </div>
  );
}

function StatusSelect({
  value,
  statusOptions,
  onChange,
  allowNone = false,
}: {
  value: string | undefined;
  statusOptions: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
  allowNone?: boolean;
}) {
  return (
    <Select
      value={value ?? (allowNone ? "none" : "")}
      onValueChange={(v) => onChange(v === "none" ? undefined : v)}
    >
      <SelectTrigger>
        <SelectValue placeholder="Pick a status" />
      </SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="none">Default</SelectItem>}
        {statusOptions.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NotifyTargetSelect({
  orgId,
  value,
  onChange,
}: {
  orgId: Id<"organizations">;
  value: AutomationNotifyTarget;
  onChange: (next: AutomationNotifyTarget) => void;
}) {
  const { data: roles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );

  return (
    <div className="grid gap-2">
      <Select
        value={value.kind}
        onValueChange={(v) => {
          if (v === "task_assignee") onChange({ kind: "task_assignee" });
          else if (v === "actor") onChange({ kind: "actor" });
          else if (v === "role") {
            onChange({ kind: "role", roleId: "" as Id<"workflowRoles"> });
          } else onChange({ kind: "user", userId: "" as Id<"users"> });
        }}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="task_assignee">
            The task&apos;s assignee
          </SelectItem>
          <SelectItem value="actor">Whoever triggered the event</SelectItem>
          <SelectItem value="role">Whoever holds a role on the job</SelectItem>
          <SelectItem value="user">A specific person</SelectItem>
        </SelectContent>
      </Select>
      {value.kind === "user" && (
        <MemberSelect
          orgId={orgId}
          value={value.userId || null}
          onChange={(next) => next && onChange({ kind: "user", userId: next })}
          placeholder="Pick a person"
        />
      )}
      {value.kind === "role" && (
        <>
          <WorkflowRoleSelect
            roles={roles ?? []}
            value={value.roleId || null}
            onChange={(next) =>
              next && onChange({ kind: "role", roleId: next })
            }
          />
          <p className="text-[11px] text-muted-foreground">
            Resolved per job — falls back to whoever triggered the event when
            nobody holds the role.
          </p>
        </>
      )}
    </div>
  );
}
